import useRandomNumber from "@/composables/useRandomNumber"
import api from "@/services/apiClient"
import { CoachAssigned, CoachForSelection } from "@/shared/types/Coach"
import { QuizCompiled } from "@/shared/types/QuizCompiled"
import { defineStore } from "pinia"

export const useCoachingStore = defineStore("coaching", {
  state: () => ({
    coaches: [] as CoachForSelection[],
    assignedCoach: null as CoachAssigned | null,
    coachingQuizData: null as QuizCompiled | null
  }),
  getters: {
    /**
     * Get coaches that have a biography, full name, and profile photo URL
     */
    getCoachesForSelectionPage(state) {
      if (!state.coaches || state.coaches.length === 0) {
        return []
      }

      const coaches = [...state.coaches]
      return coaches.filter((coach) => coach.biography && coach.fullName && coach.profilePhotoUrl)
    },

    /**
     * Get the user's assigned coach with a percent match
     */
    getAssignedCoach(state) {
      const percentMatch = useRandomNumber(state.assignedCoach?.fullName || "Full Name", 90, 98).value
      return { ...(state.assignedCoach as CoachAssigned), percentMatch }
    },

    /**
     * Generate the first name for the user's assigned coach
     */
    getAssignedCoachFirstName(state) {
      return state.assignedCoach?.fullName.split(" ")[0]
    },

    /**
     * The full name of the user's assigned coach
     */
    getAssignedCoachFullName(state) {
      return state.assignedCoach?.fullName
    },

    /**
     * Check if the user has an assigned coach
     */
    hasAssignedCoach(state) {
      return !!state.assignedCoach && !!state.assignedCoach?.id
    },

    /**
     * Check if the list of coaches is available
     */
    hasCoaches(state) {
      return state.coaches && state.coaches.length > 0
    }
  },
  actions: {
    /**
     * Load all coaches from the API
     */
    async loadCoaches() {
      try {
        const data = await api.coach.getListForSelection()
        this.coaches = data.coaches || []
      } catch (error) {
        console.error("Error loading coaches", error)
      }
    },

    /**
     * Load the user's assigned coach from the API
     */
    async loadAssignedCoach() {
      try {
        const data = await api.coach.getAssigned()
        this.assignedCoach = data.coach || null
      } catch (error) {
        console.error("Error loading assigned coach", error)
      }
    },

    /**
     * Temporarily manually set user assigned coach in coaching store to prevent reassignment.
     * Created primarily for use on CoachingUpsell page - forcing assignment of all users (even executive) to Julie even when she might not be an executive coach.
     * Not recommended to use anywhere else and should be revisited for cleanup in the future.
     */
    setAssignedCoach(coach: CoachAssigned) {
      this.assignedCoach = coach
    }
  }
})
